class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code !== 'Escape') return;
      if (this.closeUpsellVariantModal()) return;
      this.close();
    });
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initializeCartExperience();
    this.addEventListener('click', this.onCartDrawerClick.bind(this));
    this.addEventListener('input', this.onCartDrawerInput.bind(this));
    this.addEventListener('keydown', this.onCartDrawerKeydown.bind(this));
    this.addEventListener('submit', this.onCartDrawerSubmit.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.initializeCartExperience();
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.documentElement.classList.add('cart-drawer-open');
    document.body.classList.add('overflow-hidden', 'cart-drawer-open');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.documentElement.classList.remove('cart-drawer-open');
    document.body.classList.remove('overflow-hidden', 'cart-drawer-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    this.initializeCartExperience(true);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  initializeCartExperience(resetTimer = false) {
    this.syncDiscountInputs();
    this.startReserveTimer(resetTimer);
  }

  syncDiscountInputs() {
    const input = this.querySelector('[data-cart-discount-input]');
    const hiddenInput = this.querySelector('[data-cart-discount-hidden]');

    if (!input || !hiddenInput) return;

    if (this.discountCode) {
      input.value = this.discountCode;
      hiddenInput.value = this.discountCode;
    }
  }

  syncDiscountCode() {
    const input = this.querySelector('[data-cart-discount-input]');
    const hiddenInput = this.querySelector('[data-cart-discount-hidden]');
    const code = input?.value.trim() || '';

    if (hiddenInput) hiddenInput.value = code;
    this.discountCode = code;
    return code;
  }

  onCartDrawerInput(event) {
    if (!event.target.matches('[data-cart-discount-input]')) return;

    this.syncDiscountCode();
    this.renderDiscountState({ status: 'idle' });
  }

  onCartDrawerSubmit(event) {
    if (event.target.id !== 'CartDrawer-Form') return;

    this.syncDiscountCode();
  }

  onCartDrawerKeydown(event) {
    if (!event.target.matches('[data-cart-discount-input]') || event.key !== 'Enter') return;

    event.preventDefault();
    const applyButton = this.querySelector('[data-cart-discount-apply]');
    if (applyButton && !applyButton.disabled) this.applyDiscountFromDrawer(applyButton);
  }

  onCartDrawerClick(event) {
    const upsellAddButton = event.target.closest('[data-cart-upsell-add]');
    const upsellModalClose = event.target.closest('[data-cart-upsell-modal-close]');
    const upsellModalOption = event.target.closest('[data-cart-upsell-modal-option]');
    const upsellModalAdd = event.target.closest('[data-cart-upsell-modal-add]');
    const upsellPrevButton = event.target.closest('[data-cart-upsell-prev]');
    const upsellNextButton = event.target.closest('[data-cart-upsell-next]');
    const applyButton = event.target.closest('[data-cart-discount-apply]');
    const clearButton = event.target.closest('[data-cart-discount-clear]');
    const clearInputButton = event.target.closest('[data-cart-discount-clear-input]');

    if (upsellModalClose) {
      this.closeUpsellVariantModal();
      return;
    }

    if (upsellModalOption) {
      this.selectUpsellVariantOption(upsellModalOption);
      return;
    }

    if (upsellModalAdd) {
      this.addSelectedUpsellVariant(upsellModalAdd);
      return;
    }

    if (upsellPrevButton || upsellNextButton) {
      this.scrollUpsells(upsellNextButton ? 1 : -1);
      return;
    }

    if (upsellAddButton) {
      this.addUpsellToCart(upsellAddButton);
      return;
    }

    if (clearButton || clearInputButton) {
      this.clearDiscountFromDrawer();
      return;
    }

    if (!applyButton) return;

    this.applyDiscountFromDrawer(applyButton);
  }

  scrollUpsells(direction) {
    const slider = this.querySelector('[data-cart-upsell-slider]');
    if (!slider) return;

    slider.scrollBy({
      left: direction * Math.max(180, slider.clientWidth * 0.8),
      behavior: 'smooth',
    });
  }

  async addUpsellToCart(button) {
    const variantId = button.dataset.variantId;
    if (button.disabled) return;

    if (!variantId) {
      this.openUpsellVariantModal(button);
      return;
    }

    this.addVariantToCart(variantId, button);
  }

  async addVariantToCart(variantId, button) {
    if (!variantId || button.disabled) return;

    button.disabled = true;
    button.classList.add('is-loading');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append(
      'sections',
      this.getSectionsToRender().map((section) => section.id)
    );
    formData.append('sections_url', window.location.pathname);

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    try {
      const response = await fetch(routes.cart_add_url, config);
      const parsedState = await response.json();

      if (parsedState.status) {
        this.showCartError(parsedState.description || this.dataset.upsellErrorMessage || window.cartStrings.error);
        return;
      }

      this.renderContents(parsedState);
    } catch (error) {
      console.error(error);
      this.showCartError(this.dataset.upsellErrorMessage || window.cartStrings.error);
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }

  openUpsellVariantModal(button) {
    const card = button.closest('[data-cart-upsell-card]');
    const productData = card?.querySelector('[data-cart-upsell-product-json]');
    const modal = this.querySelector('[data-cart-upsell-modal]');
    const optionsContainer = modal?.querySelector('[data-cart-upsell-modal-options]');
    const addButton = modal?.querySelector('[data-cart-upsell-modal-add]');
    const title = modal?.querySelector('[data-cart-upsell-modal-title]');
    const price = modal?.querySelector('[data-cart-upsell-modal-price]');
    const image = modal?.querySelector('[data-cart-upsell-modal-image]');
    const sourceImage = card?.querySelector('.cart-drawer__upsell-image');
    const sourceTitle = card?.querySelector('.cart-drawer__upsell-name');
    const sourcePrice = card?.querySelector('.cart-drawer__upsell-price');

    if (!productData || !modal || !optionsContainer || !addButton) return;

    let product;
    try {
      product = JSON.parse(productData.textContent);
    } catch (error) {
      console.error(error);
      return;
    }

    const variants = product.variants || [];
    const availableVariants = variants.filter((variant) => variant.available);

    if (availableVariants.length === 1) {
      this.addVariantToCart(availableVariants[0].id, button);
      return;
    }

    this.upsellModalSelectedVariantId = null;
    this.upsellModalTrigger = button;

    if (title && sourceTitle) title.textContent = sourceTitle.textContent.trim();
    if (price && sourcePrice) price.textContent = sourcePrice.textContent.trim();
    if (image && sourceImage) {
      image.src = sourceImage.currentSrc || sourceImage.src;
      image.alt = sourceImage.alt || '';
    }

    optionsContainer.innerHTML = variants
      .map((variant) => {
        const label = this.escapeHTML(this.cleanVariantLabel(variant.public_title || variant.title || ''));
        const priceText = this.formatMoney(variant.price);
        const disabled = variant.available ? '' : ' disabled';
        const unavailable = variant.available ? '' : ' is-unavailable';
        return `<button type="button" class="cart-drawer__upsell-modal-option${unavailable}" data-cart-upsell-modal-option data-variant-id="${variant.id}" data-variant-price="${priceText}" aria-pressed="false"${disabled}>${label}</button>`;
      })
      .join('');

    addButton.disabled = true;
    addButton.dataset.variantId = '';
    modal.hidden = false;
    modal.classList.add('is-open');
    modal.querySelector('[data-cart-upsell-modal-close]')?.focus();
  }

  closeUpsellVariantModal() {
    const modal = this.querySelector('[data-cart-upsell-modal]');
    if (!modal || modal.hidden) return false;

    modal.hidden = true;
    modal.classList.remove('is-open');
    this.upsellModalSelectedVariantId = null;
    this.upsellModalTrigger?.focus();
    this.upsellModalTrigger = null;
    return true;
  }

  selectUpsellVariantOption(option) {
    const modal = option.closest('[data-cart-upsell-modal]');
    const addButton = modal?.querySelector('[data-cart-upsell-modal-add]');
    const price = modal?.querySelector('[data-cart-upsell-modal-price]');

    modal?.querySelectorAll('[data-cart-upsell-modal-option]').forEach((button) => {
      const isSelected = button === option;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    this.upsellModalSelectedVariantId = option.dataset.variantId;
    if (addButton) {
      addButton.disabled = false;
      addButton.dataset.variantId = this.upsellModalSelectedVariantId;
    }
    if (price && option.dataset.variantPrice) price.textContent = option.dataset.variantPrice;
  }

  addSelectedUpsellVariant(button) {
    const variantId = button.dataset.variantId || this.upsellModalSelectedVariantId;
    if (!variantId) return;

    this.addVariantToCart(variantId, button);
  }

  showCartError(message) {
    const errors = this.querySelector('#CartDrawer-CartErrors');
    if (errors) errors.textContent = message || '';
  }

  async clearDiscountFromDrawer() {
    const input = this.querySelector('[data-cart-discount-input]');
    const hiddenInput = this.querySelector('[data-cart-discount-hidden]');

    if (input) input.value = '';
    if (hiddenInput) hiddenInput.value = '';
    this.discountCode = '';

    try {
      const cart = await this.applyDiscountCode('');
      this.renderDiscountState({
        status: 'idle',
        amount: 0,
        total: cart.total_price,
        currency: cart.currency,
      });
    } catch (error) {
      console.error(error);
      this.renderDiscountState({ status: 'idle', amount: 0 });
    }
  }

  async applyDiscountFromDrawer(applyButton) {
    const input = this.querySelector('[data-cart-discount-input]');
    const hiddenInput = this.querySelector('[data-cart-discount-hidden]');
    const code = this.syncDiscountCode();

    if (!code) {
      this.renderDiscountState({ status: 'invalid' });
      return;
    }

    applyButton.disabled = true;
    this.renderDiscountState({ status: 'loading' });

    try {
      const cart = await this.applyDiscountCode(code);
      const appliedDiscounts = this.getAppliedDiscounts(cart);
      const normalizedCode = this.normalizeDiscountCode(code);
      const matchingDiscount = appliedDiscounts.find(
        (discount) => this.normalizeDiscountCode(discount.title) === normalizedCode
      );

      if (matchingDiscount || cart.total_discount > 0) {
        this.discountCode = matchingDiscount?.title || code;
        if (hiddenInput) hiddenInput.value = this.discountCode;
        if (input) input.value = this.discountCode;
        this.renderDiscountState({
          status: 'valid',
          code: this.discountCode,
          amount: cart.total_discount,
          total: cart.total_price,
          currency: cart.currency,
        });
      } else {
        if (hiddenInput) hiddenInput.value = '';
        this.discountCode = '';
        this.renderDiscountState({
          status: 'invalid',
          amount: 0,
          total: cart.total_price,
          currency: cart.currency,
        });
      }
    } catch (error) {
      console.error(error);
      if (hiddenInput) hiddenInput.value = '';
      this.discountCode = '';
      this.renderDiscountState({ status: 'invalid' });
    } finally {
      applyButton.disabled = false;
    }
  }

  async applyDiscountCode(code) {
    const updateBody = JSON.stringify({ discount: code });

    await fetch(routes.cart_update_url, {
      ...fetchConfig(),
      body: updateBody,
    });

    let cart = await this.fetchCart();
    const hasDiscount = this.getAppliedDiscounts(cart).length > 0 || cart.total_discount > 0;
    if (hasDiscount) return cart;

    await fetch(`/discount/${encodeURIComponent(code)}?redirect=/cart.js`, {
      credentials: 'same-origin',
      cache: 'no-store',
    });

    cart = await this.fetchCart();
    return cart;
  }

  async fetchCart() {
    const response = await fetch(`${routes.cart_url}.js`, {
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) throw new Error('Unable to fetch cart');
    return response.json();
  }

  getAppliedDiscounts(cart) {
    const cartDiscounts = cart.cart_level_discount_applications || [];
    const lineDiscounts = (cart.items || []).flatMap((item) => item.discounts || []);
    return [...cartDiscounts, ...lineDiscounts].filter((discount) => discount && discount.title);
  }

  normalizeDiscountCode(code) {
    return String(code || '').trim().toLowerCase();
  }

  renderDiscountState({ status, code, amount, total, currency } = {}) {
    const input = this.querySelector('[data-cart-discount-input]');
    const field = this.querySelector('[data-cart-discount-field]');
    const message = this.querySelector('[data-cart-discount-message]');
    const row = this.querySelector('[data-cart-discount-row]');
    const codes = this.querySelector('[data-cart-discount-codes]');
    const amountElement = this.querySelector('[data-cart-discount-amount]');
    const totalElement = this.querySelector('[data-cart-total]');

    if (field) {
      field.classList.toggle('is-invalid', status === 'invalid');
      field.classList.toggle('is-loading', status === 'loading');
    }

    if (message) {
      message.textContent = status === 'invalid' ? this.dataset.discountInvalidMessage || '' : '';
    }

    if (status === 'valid' && row && codes && amountElement) {
      row.classList.remove('hidden');
      codes.innerHTML = `
        <span class="cart-drawer__discount-chip">
          <svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12">
            <path fill="currentColor" d="M6.5 1 11 5.5 5.5 11 1 6.5V2.8C1 1.8 1.8 1 2.8 1h3.7Zm-3.3 3.3a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"/>
          </svg>
          ${this.escapeHTML(code)}
          <button type="button" aria-label="${this.escapeHTML(this.dataset.discountRemoveLabel || '')}" data-cart-discount-clear>×</button>
        </span>
      `;
      amountElement.textContent = amount > 0 ? `- ${this.formatMoney(amount, currency)}` : '';
    } else if ((status === 'idle' || status === 'invalid') && row && amount === 0) {
      row.classList.add('hidden');
      if (codes) codes.innerHTML = '';
      if (amountElement) amountElement.textContent = '';
    }

    if (totalElement && Number.isFinite(total)) {
      totalElement.textContent = this.formatMoney(total, currency);
    }

    if (input && status === 'invalid') input.focus();
  }

  formatMoney(cents, currency = 'EUR') {
    const amount = Number(cents || 0) / 100;
    const formattedAmount = amount.toLocaleString('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return currency === 'EUR' ? `${formattedAmount}€` : `${formattedAmount} ${currency}`;
  }

  escapeHTML(value) {
    return String(value || '').replace(/[&<>"']/g, (character) => {
      const entities = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      };
      return entities[character];
    });
  }

  cleanVariantLabel(value) {
    return String(value || '').replace(/⚡️|⚡|🔵/g, '').trim();
  }

  startReserveTimer(resetTimer = false) {
    const timers = this.querySelectorAll('[data-cart-reserve-timer]');
    if (!timers.length) return;

    if (resetTimer || !this.reserveEndsAt) {
      this.reserveEndsAt = Date.now() + 10 * 60 * 1000;
    }

    this.querySelectorAll('[data-cart-reserve-active]').forEach((activeMessage) => {
      activeMessage.classList.remove('hidden');
    });
    this.querySelectorAll('[data-cart-reserve-expired]').forEach((expiredMessage) => {
      expiredMessage.classList.add('hidden');
    });

    const updateTimer = () => {
      const remaining = Math.max(0, this.reserveEndsAt - Date.now());
      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      timers.forEach((timer) => {
        timer.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      });

      if (remaining <= 0 && this.reserveTimerInterval) {
        clearInterval(this.reserveTimerInterval);
        this.reserveTimerInterval = null;
        this.showReserveExpiredState();
      }
    };

    if (this.reserveTimerInterval) clearInterval(this.reserveTimerInterval);
    updateTimer();
    this.reserveTimerInterval = setInterval(updateTimer, 1000);
  }

  showReserveExpiredState() {
    this.querySelectorAll('[data-cart-reserve-active]').forEach((activeMessage) => {
      activeMessage.classList.add('hidden');
    });
    this.querySelectorAll('[data-cart-reserve-expired]').forEach((expiredMessage) => {
      expiredMessage.classList.remove('hidden');
    });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    super.connectedCallback();
    document.querySelector('cart-drawer')?.initializeCartExperience();
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
